const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Temporary 72-hour window configuration:
 * Allows the last 5 completed months (e.g. April, May, June, July, August)
 * for 72 hours (starts Sep 8, 2026, 11:30 AM IST).
 * Once expired (Sep 11, 2026, 11:30 AM IST), automatically reverts to the
 * standard 2 completed months (July, August) without requiring code changes.
 */
export const EXTENDED_MONTHS_COUNT = 5;
export const DEFAULT_MONTHS_COUNT = 2;

export const EXTENDED_WINDOW_START_TIME = new Date('2026-09-08T11:30:00+05:30').getTime();
export const EXTENDED_WINDOW_DURATION_HOURS = 72;
export const EXTENDED_WINDOW_EXPIRY_TIME =
  EXTENDED_WINDOW_START_TIME + EXTENDED_WINDOW_DURATION_HOURS * 60 * 60 * 1000;

/**
 * Helper to safely parse dates, timestamps, or invalid dates into valid Date instances.
 */
const parseDate = (d) => {
  if (typeof d === 'number' && !Number.isNaN(d)) {
    return new Date(d);
  }
  if (d instanceof Date && !Number.isNaN(d.getTime())) {
    return d;
  }
  return new Date();
};

/**
 * Detects active reimbursement type from DOM if available in browser context.
 * Returns 'hubs', 'hubsetup', or 'employees'.
 */
export const detectReimbursementType = () => {
  if (typeof document !== 'undefined') {
    try {
      const selects = document.querySelectorAll('select');
      for (let i = 0; i < selects.length; i++) {
        const select = selects[i];
        if (select.querySelector('option[value="hubsetup"]') || select.querySelector('option[value="hubs"]')) {
          if (select.value) return select.value;
        }
      }

      const bodyText = document.body ? document.body.innerText || '' : '';
      if (bodyText.includes('Hub Setup reimbursement')) return 'hubsetup';
      if (bodyText.includes('Employee Travel Reimbursement')) return 'employees';
      if (bodyText.includes('Hubs reimbursement')) return 'hubs';
    } catch {
      // Ignore DOM query errors
    }
  }
  return 'hubs';
};

/**
 * Checks if the temporary 72-hour extended months window is currently active.
 * Only applies to 'hubs' reimbursement type (not travel or hubsetup).
 * Supports manual override via localStorage ('EXPENSE_MONTHS_EXTENDED' = 'true'/'false') for testing.
 * @param {string|Date} [typeOrDate]
 * @param {Date|number} [currentDate]
 * @returns {boolean}
 */
export const isExtendedMonthWindowActive = (typeOrDate, currentDate) => {
  let type = typeOrDate;
  let date = currentDate;

  if (typeOrDate instanceof Date || (typeof typeOrDate === 'number' && !Number.isNaN(typeOrDate))) {
    date = typeOrDate;
    type = detectReimbursementType();
  } else if (!type || typeof type !== 'string') {
    type = detectReimbursementType();
  }

  const validDate = parseDate(date);

  // Extended months only apply to hub reimbursement, NOT travel ('employees') or hub setup ('hubsetup')
  if (type !== 'hubs') {
    return false;
  }

  if (typeof window !== 'undefined') {
    try {
      const override = window.localStorage?.getItem('EXPENSE_MONTHS_EXTENDED');
      if (override === 'true') return true;
      if (override === 'false') return false;
    } catch {
      // Storage access blocked; fallback to timer
    }
  }

  const now = validDate.getTime();
  return now >= EXTENDED_WINDOW_START_TIME && now < EXTENDED_WINDOW_EXPIRY_TIME;
};

/**
 * Selectable completed months (excluding current month).
 * - For 'hubs' during 72h window: returns last 5 completed months (e.g. April, May, June, July, August in September).
 * - For 'hubs' after 72h timer expires: automatically reverts to last 2 completed months (e.g. July, August).
 * - For 'hubsetup' and 'employees': always returns standard 2 completed months (e.g. July, August).
 * @param {string|Date} [typeOrDate]
 * @param {Date|number} [currentDate]
 * @returns {string[]}
 */
export const getSelectableMonths = (typeOrDate, currentDate) => {
  let type = typeOrDate;
  let date = currentDate;

  if (typeOrDate instanceof Date || (typeof typeOrDate === 'number' && !Number.isNaN(typeOrDate))) {
    date = typeOrDate;
    type = detectReimbursementType();
  } else if (!type || typeof type !== 'string') {
    type = detectReimbursementType();
  }

  const validDate = parseDate(date);
  const currentMonthIndex = validDate.getMonth();
  const count = isExtendedMonthWindowActive(type, validDate) ? EXTENDED_MONTHS_COUNT : DEFAULT_MONTHS_COUNT;
  const months = [];

  for (let i = count; i >= 1; i--) {
    const monthIndex = (currentMonthIndex - i + 12) % 12;
    months.push(MONTH_NAMES[monthIndex]);
  }

  return months;
};

/**
 * Minimum allowable bill date for date picker input.
 * - During 72h window (for hubs): allows dates starting from the 1st day of the earliest completed month (April 1st).
 * - After expiration or non-hub types: allows dates from the 1st day of 2 completed months back (July 1st).
 * Uses local calendar date components to avoid timezone offset shifts.
 * @param {string|Date} [typeOrDate]
 * @param {Date|number} [currentDate]
 * @returns {string} YYYY-MM-DD
 */
export const getMinBillDate = (typeOrDate, currentDate) => {
  let type = typeOrDate;
  let date = currentDate;

  if (typeOrDate instanceof Date || (typeof typeOrDate === 'number' && !Number.isNaN(typeOrDate))) {
    date = typeOrDate;
    type = detectReimbursementType();
  } else if (!type || typeof type !== 'string') {
    type = detectReimbursementType();
  }

  const validDate = parseDate(date);
  const count = isExtendedMonthWindowActive(type, validDate) ? EXTENDED_MONTHS_COUNT : DEFAULT_MONTHS_COUNT;
  const minDate = new Date(validDate.getFullYear(), validDate.getMonth() - count, 1);

  const yyyy = minDate.getFullYear();
  const mm = String(minDate.getMonth() + 1).padStart(2, '0');
  const dd = String(minDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Travel reimbursement: previous completed months plus the current month.
 * Always shows standard 2 completed months plus current month (e.g. July, August, September).
 * Does not show extended 5 months for travel.
 * @param {string|Date} [typeOrDate]
 * @param {Date|number} [currentDate]
 * @returns {string[]}
 */
export const getTravelSelectableMonths = (typeOrDate, currentDate) => {
  let date = currentDate;
  if (typeOrDate instanceof Date || typeof typeOrDate === 'number') {
    date = typeOrDate;
  }
  const validDate = parseDate(date);
  const currentMonth = MONTH_NAMES[validDate.getMonth()];
  const previousMonths = getSelectableMonths('employees', validDate);

  return [...new Set([...previousMonths, currentMonth])];
};

const SINGLE_USE_TRAVEL_BILL_TYPES = ['travel', 'accommodation'];

/** Hide Travel/Accommodation in other bill rows once already selected (travel reimb only). */
export const getAvailableEmployeeBillOptions = (bills, currentIndex, allOptions) => {
  const usedTypes = new Set(
    bills
      .filter((_, i) => i !== currentIndex)
      .map((bill) => bill.type)
      .filter((type) => SINGLE_USE_TRAVEL_BILL_TYPES.includes(type)),
  );

  return allOptions.filter(
    (option) => !usedTypes.has(option.value) || bills[currentIndex]?.type === option.value,
  );
};

/** @deprecated Use getSelectableMonths — kept for existing imports. */
export const getLastFourMonths = () => getSelectableMonths();
